/* eslint-disable @typescript-eslint/no-explicit-any */
import { TrackingEvent } from "../events";
import BaseTrackingAdapter, { PixelConfig } from "./BaseTrackingAdapter";

export interface GoogleAnalyticsConfig extends PixelConfig {
    googleAdsId?: string;
    conversionLabel?: string;
}

// Google Analytics Adapter
export default class GoogleAnalyticsAdapter extends BaseTrackingAdapter {
    private static instances: Set<string> = new Set();
    private static gtagInitialized: boolean = false;

    protected config: GoogleAnalyticsConfig;

    constructor(config: GoogleAnalyticsConfig) {
      super(config);
      this.config = config;
    }

    private getPageLocation(path: string): string {
      return new URL(path, window.location.origin).toString();
    }

    async initialize(): Promise<void> {
      if (this.initialized) return;

      // Initialize dataLayer and gtag function once
      if (!GoogleAnalyticsAdapter.gtagInitialized) {
        (window as any).dataLayer = (window as any).dataLayer || [];

        if (!(window as any).gtag) {
          // Must use `arguments` (not rest params) — gtag.js ignores Array-typed
          // dataLayer entries and only processes Arguments objects as commands.
          (window as any).gtag = function () {
            // eslint-disable-next-line prefer-rest-params
            (window as any).dataLayer.push(arguments);
          };
        }
        const gtag = (window as any).gtag;
        gtag('js', new Date());
        GoogleAnalyticsAdapter.gtagInitialized = true;
      }

      // Load a gtag script per unique measurement ID
      if (!GoogleAnalyticsAdapter.instances.has(this.config.id)) {
        const script = document.createElement('script');
        script.src = `https://www.googletagmanager.com/gtag/js?id=${this.config.id}`;
        script.async = true;
        document.head.appendChild(script);

        const gtag = (window as any).gtag;
        gtag('config', this.config.id, {
          send_page_view: false,
        });
        GoogleAnalyticsAdapter.instances.add(this.config.id);
      }

      if (this.config.googleAdsId && !GoogleAnalyticsAdapter.instances.has(this.config.googleAdsId)) {
        const gtag = (window as any).gtag;
        gtag('config', this.config.googleAdsId);
        GoogleAnalyticsAdapter.instances.add(this.config.googleAdsId);
      }

      this.initialized = true;
    }

    async trackEvent(event: TrackingEvent): Promise<void> {
      if (!this.initialized) throw new Error('Google Analytics not initialized');

      const gtag = (window as any).gtag;
      if (!gtag) throw new Error('Google Analytics gtag function not found');

      // Common parameters for all events
      const baseParams = {
        send_to: this.config.id
      };

      switch (event.type) {
        case 'PAGE_VIEW':
          
          gtag('event', 'page_view', {
            ...baseParams,
            page_title: event.payload.title,
            page_location: this.getPageLocation(event.payload.path),
            page_path: event.payload.path,
            page_referrer: event.payload.referrer,
          });
          break;

        case 'VIEW_ITEM':
          gtag('event', 'view_item', {
            ...baseParams,
            currency: event.payload.currency,
            value: event.payload.price * (event.payload.quantity || 1),
            items: [{
              item_id: event.payload.productId,
              item_name: event.payload.name,
              price: event.payload.price,
              quantity: event.payload.quantity || 1
            }]
          });
          break;

        case 'ADD_TO_CART':
          gtag('event', 'add_to_cart', {
            ...baseParams,
            currency: event.payload.currency,
            value: event.payload.price * event.payload.quantity,
            items: [{
              item_id: event.payload.productId,
              item_name: event.payload.name,
              price: event.payload.price,
              quantity: event.payload.quantity
            }]
          });
          break;

        case 'REMOVE_FROM_CART':
          gtag('event', 'remove_from_cart', {
            ...baseParams,
            currency: event.payload.currency,
            value: event.payload.price * event.payload.quantity,
            items: [{
              item_id: event.payload.productId,
              item_name: event.payload.name,
              price: event.payload.price,
              quantity: event.payload.quantity
            }]
          });
          break;

        case 'BEGIN_CHECKOUT':
          gtag('event', 'begin_checkout', {
            ...baseParams,
            currency: event.payload.currency,
            value: event.payload.totalValue,
            items: event.payload.items.map(item => ({
              item_id: item.productId,
              item_name: item.name,
              price: item.price,
              quantity: item.quantity
            }))
          });
          break;

        case 'PURCHASE':
          gtag('event', 'purchase', {
            ...baseParams,
            transaction_id: event.payload.transactionId,
            currency: event.payload.currency,
            value: event.payload.totalValue,
            tax: event.payload.tax,
            shipping: event.payload.shipping,
            items: event.payload.items.map(item => ({
              item_id: item.productId,
              item_name: item.name,
              price: item.price,
              quantity: item.quantity
            }))
          });

          if(this.config.googleAdsId) {
            gtag('event', 'conversion', {
              'send_to': `${this.config.googleAdsId}/${this.config.conversionLabel}`,
              'value': event.payload.totalValue,
              'currency': event.payload.currency,
              'transaction_id': event.payload.transactionId,
            });
          }
          break;

        case 'CUSTOM':
          gtag('event', event.payload.eventName, {
            ...baseParams,
            ...event.payload,
          });
          break;
      }
    }
}
